import { ErrorHandler, Injectable, Injector, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngxs/store';
import { SpinnerService } from 'src/app/services/spinner.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { EventId, LogLevel, setTimeout$ } from 'src/app/core/common';
import { httpErrorDiagnostics } from './http.interceptor';

@Injectable()
export class ErrorInterceptor implements ErrorHandler {
  constructor(
    private location: Location, private http: HttpClient,
    @Inject(Injector) private readonly injector: Injector, @Inject(PLATFORM_ID) platformId: any,
    private spinner: SpinnerService, private updates: SwUpdate) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.updates.isEnabled) {
      const updatesAvailable = updates.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        map(evt => ({
          type: 'UPDATE_AVAILABLE',
          current: evt.currentVersion,
          available: evt.latestVersion,
        })));

      updatesAvailable.subscribe(event => this.newVersion = !!event.available);
    }
  }

  newVersion: boolean;
  isBrowser: boolean;

  private get toastr(): ToastrService {
    return this.injector.get(ToastrService);
  }
  private get store(): Store {
    return this.injector.get(Store);
  }

  async handleError(err: Error | HttpErrorResponse) {
    // if (err?.message == 'Request cancelled') return;

    const user = this.store.selectSnapshot(state => state.auth.user);

    if (this.newVersion) {
      console.log('Error on old version');
      // this.toastr.error('شما از نسخه قدیمی نرم افزار استفاده میکنید. لطفا جهت بروزرسانی کلیک کنید')
      //   .onTap.subscribe(() => this.updates.activated.subscribe());
      // return;
    }

    let error: any = err;
    let url = this.location.path();
    let message = '';
    let title = '';
    let stack = error?.stack;
    let showToast = false;
    let toastType = 'error';
    var errorCode = Math.floor(Math.random() * 100000);
    try {
      const rejection = error?.rejection ?? error;
      const event = rejection instanceof HttpErrorResponse ? rejection.error : rejection;
      const progressEvent = event?.constructor?.name === 'ProgressEvent' ? event : undefined;
      const request = progressEvent?.target as XMLHttpRequest;
      const diagnosticError = progressEvent ?? (rejection instanceof HttpErrorResponse ? rejection : undefined);
      const diagnostics = diagnosticError ? httpErrorDiagnostics.get(diagnosticError) : undefined;
      const requestUrl = diagnostics?.requestUrl
        ?? (rejection instanceof HttpErrorResponse ? rejection.url : undefined)
        ?? (<any>request)?.__zone_symbol__xhrURL
        ?? request?.responseURL;
      if (diagnostics && rejection instanceof HttpErrorResponse && rejection.error == null) {
        // Add visibility for formerly null rejections without introducing new toasts
        // or suppressing reports through the existing HTTP status handling (e.g. 410).
        error = rejection;
        url = requestUrl || url;
        message = rejection.message || `HTTP request failed (status ${rejection.status})`;
      } else if (error?.rejection && error.rejection instanceof HttpErrorResponse) {
        error = error.rejection;
        url = requestUrl || url;
        if (error.error && error.error.constructor.name == 'ErrorEvent') {
          // A client-side or network error occurred. Handle it accordingly.
          message = error.error.message;
          title = 'اتصال اینترنت را بررسی کنید';
        } else {
          if (error.name == "HttpErrorResponse") {
            switch (error.status) {
              case 0:
                title = 'اتصال اینترنت را بررسی کنید';
                break;
              case 401:
                title = 'شما از سیستم خارج شده اید یا دسترسی به این عملیات را ندارید';
                // do not automatically logout
                // await this.store.dispatch(Logout).toPromise();
                break;
              case 404:
                title = 'محتوای مورد انتظار یافت نشد.';
                toastType = 'warning';
                break;
              case 410: return; // GONE
              case 413:
                title = 'حجم فایل ارسالی بزرگتر از حداکثر مجاز است';
                toastType = 'warning';
                break;
              case 500:
                title = `یک خطای سیستمی رخ داده است. کد خطا: ${errorCode}`;
                break;
              default:
                title = `یک خطا رخ داده است. کد خطا: ${errorCode}`;
                break;
            }
          } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            message = error.error ? (error.error.message || error.error.constructor.name) : (error.message || error.constructor.name);
            title = `خطا. Status: ${error.status}`;
          }

        }
        showToast = true;
      } else {

        if (progressEvent) {
          url = requestUrl || url;
          message = `HTTP request failed (${progressEvent.type || 'unknown event'})`;
        } else {
          const rejection = error?.rejection;
          if (rejection && rejection.message) {
            message = `${rejection.name}: ${rejection.message}`;
            stack += '\n\nrejection.stack:\n' + rejection.stack;
          }
          else
            try { message = error?.message || JSON.stringify(error || 'error is NULL'); } 
            catch { message = error?.message || `cannot stringify error (${typeof error})`; }
        
        }
      }

      var extra = '';
      try { extra = JSON.stringify(error?.reason) } catch { }
      try { extra += '\n\n' + JSON.stringify(error?.promise); } catch { }

      const args = {
        interceptor: 'ErrorInterceptor',
        url,
        pageUrl: this.location.path(),
        requestUrl,
        requestMethod: diagnostics?.requestMethod,
        status: diagnostics?.status ?? (rejection instanceof HttpErrorResponse ? rejection.status : request?.status),
        statusText: diagnostics?.statusText ?? (rejection instanceof HttpErrorResponse ? rejection.statusText : request?.statusText),
        eventType: progressEvent?.type,
        online: this.isBrowser && typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        rejectionType: rejection?.constructor?.name,
        errorType: error?.constructor.name,
        message,
        title,
        stack,
        extra
      };
      console.error(error || args);

      this.spinner.hideAll();

      if (this.isBrowser) {

        if (message && message.indexOf) {
          // suppress error toast in IOS
          if (message.indexOf('.getSubscription') > -1) { return; }

          // handle ChunkLoadError
          const chunkFailedMessage = /Loading chunk [\d]+ failed/;
          if (message.indexOf('ChunkLoadError') > -1 || chunkFailedMessage.test(message + error.message)) {
            location.reload();
            return;
          }
        }

        if (showToast) {
          switch (toastType) {
            case 'warning':
              this.toastr.warning(message, title);
              break;
            default:
              this.toastr.error(message + '. در صورت تکرار صفحه را مجددا بارگذاری کنید.', title);
              break;
          }
        }

        this.log(LogLevel.Error, message, { Id: errorCode, Name: 'Angular ErrorInterceptor' }, args);

        if (user?.Operator || user?.Karpasha)
          this.toastr.error(`کد خطا: '${errorCode}'. پیغام: ${message}`, title);
      }

    } catch (ex) {
      console.error({
        interceptor: 'ErrorInterceptor Exception',
        ex: ex,
        url: url,
        internal: error
      });
      if (user?.Operator || user?.Karpasha)
        this.toastr.error(`کد خطا: '${errorCode}'. پیغام: ${message}, ex: ${ex}`, title);

    }
  }

  log(level: LogLevel, message: string, eventId?: EventId, data?: { [key: string]: any }) {
    data._appversion = environment.app.version;
    data._clientdate = new Date().toLocaleString();
    setTimeout$(() => {
      try {
        var log = { level, message, eventId, data };
        if (!environment.production) console.log('sharedService.Log', log);
        else this.http.post(`${environment.app.apiUrl}shared/log`, log).subscribe({
          error: error => console.error('Error report submission failed', error)
        });
      }
      catch (e) { console.error(e); }
    });
  }

}
